import {identity} from '@packages/utilities/fp/utils';
import {matchJson} from '@packages/utilities/json';
import {
  Plugins,
  TypeImplementation,
  Result,
  Type,
} from '@packages/systems/wfdl';
import {
  ELiteral,
  EUnion,
  ECase,
  ERecord,
  TUnion,
  TRecord,
  TEmptyRecord,
  TTypeConstructor,
  TNumber,
  TText,
  TList,
  functionOf,
} from '@packages/systems/wfdl-ast';
import {call1, func, $, _$} from '@packages/utilities/wfdl-helpers';
import {
  typeName,
  pluginName,
  iconName,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {toBooleanTag, TBoolean} from '@packages/systems/plugins-utils/Boolean';
import {TOption} from '@packages/systems/plugins/Option';
import {DataTypeJson12} from '@packages/systems/ui/icons';

const jsonTypeName = qualify(Plugins.name('Json'), typeName('Json'));
export const TJson = TTypeConstructor(jsonTypeName);

const NameValuePairs = TList(
  TRecord({
    Name: TText,
    Value: TJson,
  })
);

const JsonShape = TUnion({
  Null: TEmptyRecord,
  Boolean: TBoolean,
  Number: TNumber,
  String: TText,
  Array: TList(TJson),
  Object: NameValuePairs,
});

export const unwrap = call1(_$('Json', 'Unwrap'));

const none = func(['_'], EUnion({tag: 'None', value: ERecord({})}));
const some = func(['x'], EUnion({tag: 'Some', value: $('x')}));

const getter = (tag: string, ty: Type) => {
  const cases = {
    Null: none,
    Boolean: none,
    Number: none,
    String: none,
    Array: none,
    Object: none,
  } as const;
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly Null: any; readonly Boolean: any; readonly Number: any; readonly String: any; readonly Array: any; readonly Object: any; }'.
  cases[tag] = some;
  return {
    envType: {
      dataType: functionOf(TJson, TOption(ty)),
      exported: 'hidden',
    },
    expression: func(
      ['json'],
      ECase({
        value: unwrap($('json')),
        cases,
      })
    ),
  };
};

export const plugin = Plugins.PrivilegedPlugin({
  icons: {
    Json: DataTypeJson12,
  },
  dataTypes: {
    Json: TypeImplementation.Custom({
      parseValue: Result.Ok,
      render: () => null,
      icon: TypeImplementation.IconByName(
        qualify(pluginName('Json'), iconName('Json'))
      ),
    }),
  },

  values: {
    Null: {
      envType: {
        dataType: TJson,
        exported: 'hidden',
      },
      // Using an expression instead of `value: null` because
      // `Plugins.bootstrap` does not handle `null` properly.
      expression: ELiteral({
        name: jsonTypeName,
        value: null,
      }),
    },
    'From Boolean': {
      envType: {
        dataType: functionOf(TBoolean, TJson),
        exported: 'hidden',
      },
      // @ts-expect-error - TS7031 - Binding element 'tag' implicitly has an 'any' type.
      value: ({tag}) => tag === 'True',
    },
    'From Number': {
      envType: {
        dataType: functionOf(TNumber, TJson),
        exported: 'hidden',
      },
      value: identity,
    },
    'From Text': {
      envType: {
        dataType: functionOf(TText, TJson),
        exported: 'hidden',
      },
      value: identity,
    },
    'From List': {
      envType: {
        dataType: functionOf(TList(TJson), TJson),
        exported: 'hidden',
      },
      value: identity,
    },
    'From Name & Value pairs': {
      envType: {
        dataType: functionOf(NameValuePairs, TJson),
        exported: 'hidden',
      },
      // @ts-expect-error - TS7006 - Parameter 'pairs' implicitly has an 'any' type.
      value: (pairs) =>
        // @ts-expect-error - TS2347 - Untyped function calls may not accept type arguments. | TS7006 - Parameter 'acc' implicitly has an 'any' type. | TS7031 - Binding element 'Name' implicitly has an 'any' type. | TS7031 - Binding element 'Value' implicitly has an 'any' type.
        pairs.reduce<Record<string, any>>((acc, {Name, Value}) => {
          acc[Name] = Value;
          return acc;
        }, {}),
    },
    Unwrap: {
      envType: {
        dataType: functionOf(TJson, JsonShape),
        exported: 'hidden',
      },
      value: matchJson({
        Null: (_) => ({tag: 'Null', val: {}}),
        Boolean: (bool) => ({tag: 'Boolean', val: toBooleanTag(bool)}),
        Number: (num) => ({tag: 'Number', val: num}),
        String: (str) => ({tag: 'String', val: str}),
        Array: (arr) => ({tag: 'Array', val: arr}),
        Object: (obj) => ({
          tag: 'Object',
          val: Object.entries(obj).map(([Name, Value]: [any, any]) => ({
            Name,
            Value,
          })),
        }),
      }),
    },
    // @ts-expect-error - TS2322 - Type '{ envType: { dataType: Type; exported: string; }; expression: any; }' is not assignable to type 'TypedValue'.
    'To Boolean': getter('Boolean', TBoolean),
    // @ts-expect-error - TS2322 - Type '{ envType: { dataType: Type; exported: string; }; expression: any; }' is not assignable to type 'TypedValue'.
    'To Number': getter('Number', TNumber),
    // @ts-expect-error - TS2322 - Type '{ envType: { dataType: Type; exported: string; }; expression: any; }' is not assignable to type 'TypedValue'.
    'To Text': getter('String', TText),
    // @ts-expect-error - TS2322 - Type '{ envType: { dataType: Type; exported: string; }; expression: any; }' is not assignable to type 'TypedValue'.
    'To List': getter('Array', TList(TJson)),
    // @ts-expect-error - TS2322 - Type '{ envType: { dataType: Type; exported: string; }; expression: any; }' is not assignable to type 'TypedValue'.
    'To Name & Value pairs': getter('Object', NameValuePairs),
    Parse: {
      envType: {
        dataType: functionOf(
          TText,
          TUnion({
            Error: TText,
            Ok: TJson,
          })
        ),
        exported: 'hidden',
      },
      // @ts-expect-error - TS7006 - Parameter 'string' implicitly has an 'any' type.
      value: (string) => {
        try {
          return {tag: 'Ok', val: JSON.parse(string)};
        } catch (err: any) {
          return {tag: 'Error', val: err.message};
        }
      },
    },
  },
});

export const parse = call1(_$('Json', 'Parse'));

export const toBooleanRef = _$('Json', 'To Boolean');
export const toBoolean = call1(toBooleanRef);

export const fromBooleanRef = _$('Json', 'From Boolean');
export const fromBoolean = call1(fromBooleanRef);

export const fromTextRef = _$('Json', 'From Text');
export const fromText = call1(fromTextRef);

export const fromListRef = _$('Json', 'From List');
export const fromList = call1(fromListRef);

export const fromNameValuePairsRef = _$('Json', 'From Name & Value pairs');
export const fromNameValuePairs = call1(fromNameValuePairsRef);

export const toNumberRef = _$('Json', 'To Number');
export const toNumber = call1(toNumberRef);

export const toTextRef = _$('Json', 'To Text');
export const toText = call1(toTextRef);

export const toListRef = _$('Json', 'To List');
export const toList = call1(toListRef);

export const toNameValuePairsRef = _$('Json', 'To Name & Value pairs');
export const toNameValuePairs = call1(toNameValuePairsRef);
